import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useT } from "@/i18n";
import { useLandingTheme } from "@/context/LandingThemeContext";

const WHATSAPP_LINK = "https://wa.me/819045660098?text=Ol%C3%A1%2C%20gostaria%20de%20comprar%20um%20plano%20NewSky%20Mobile";
const SIM_CARD_URL = "https://customer-assets.emergentagent.com/job_japao-conectado/artifacts/lr8ayo8x_Carta%CC%83o%20SIM%20futurista%20em%20To%CC%81quio.png";

export default function HeroSection() {
  const t = useT();
  const { theme } = useLandingTheme();
  const L = theme === "light";
  const textMain = L ? "text-slate-900" : "text-white";
  const textSub = L ? "text-slate-500" : "text-slate-400";
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-20 pb-8">

      {/* Background orbs */}
      <div className="hero-gradient-orb-1 -top-32 -left-32 sm:left-10" />
      <div className="hero-gradient-orb-2 top-1/3 -right-48 sm:right-0" />


      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
          {/* Left: Text */}
          <div className="flex-1 text-center lg:text-left">
            {/* Brand name */}
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              data-testid="hero-brand-name"
              className={`text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight ${textMain} mb-6 sm:mb-8`}>
              NewSky <span className="gradient-text">{t.hero.brand}</span>
            </motion.h2>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              data-testid="hero-headline"
              className={`text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.1] ${textMain} mb-6 sm:mb-8`}>
              {t.hero.headline}{" "}
              <span className="gradient-text">{t.hero.headlineAccent}</span>
            </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          data-testid="hero-subheadline"
          className={`text-base sm:text-lg lg:text-xl ${textSub} max-w-2xl mx-auto lg:mx-0 mb-10 sm:mb-12 leading-relaxed`}>
          {t.hero.sub}
          <br className="hidden sm:block" />
          {t.hero.sub2}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-medium text-white btn-gradient text-base w-full sm:w-auto">

            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            {t.hero.ctaBuy}
          </a>
          <a
            href="#planos"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#planos")?.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="hero-plans-button"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-medium text-white btn-glass text-base w-full sm:w-auto">

            {t.hero.ctaPlans}
          </a>
        </motion.div>
          </div>

          {/* Right: SIM Card Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, x: 40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex-shrink-0 hidden lg:flex lg:flex-col lg:items-center"
          >
            {/* Overline above SIM card */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-base sm:text-lg font-bold uppercase tracking-[0.25em] text-orange-400 mb-8 text-center">
              {t.hero.overline}
            </motion.p>
            <div className="relative">
              <img
                src={SIM_CARD_URL}
                alt="NewSky Mobile SIM Card"
                data-testid="hero-sim-card"
                className="relative w-[440px] xl:w-[520px] h-auto"
                style={{ imageRendering: "high-quality" }}
              />
            </div>
          </motion.div>

          {/* Mobile: Overline + SIM card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="lg:hidden mt-4 text-center"
          >
            <p className="text-sm sm:text-base font-bold uppercase tracking-[0.25em] text-orange-400 mb-6">
              {t.hero.overline}
            </p>
            <img
              src={SIM_CARD_URL}
              alt="NewSky Mobile SIM Card"
              className="w-full max-w-md h-auto mx-auto"
              style={{ imageRendering: "high-quality" }}
            />
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            <ChevronDown className="w-6 h-6 text-slate-600" strokeWidth={1.5} />
          </motion.div>
        </motion.div>
      </div>
    </section>);
}